import React, { useEffect } from "react";
import { motion, useAnimation } from "framer-motion";
import { HelmetProvider } from "react-helmet-async";

const NotFound = () => {
  const titleControls = useAnimation();
  const subtitleControls = useAnimation();

  // Glitch animation sequence
  useEffect(() => {
    const glitchSequence = async () => {
      while (true) {
        // Normal state
        await titleControls.start({ x: 0, opacity: 1, skew: 0 });
        await new Promise((resolve) =>
          setTimeout(resolve, Math.random() * 5000 + 2000)
        ); // Random delay between glitches

        // Fast glitch sequence
        for (let i = 0; i < 3; i++) {
          // Glitch left with color shift
          await titleControls.start({
            x: Math.random() * 10 - 5,
            opacity: 0.9,
            skew: Math.random() * 5 - 2.5,
            textShadow: "2px 0 #ff0, -2px 0 #0ff",
            transition: { duration: 0.1 },
          });

          // Glitch right
          await titleControls.start({
            x: Math.random() * 10 - 5,
            opacity: 0.9,
            skew: Math.random() * -5 + 2.5,
            textShadow: "-2px 0 #ff0, 2px 0 #0ff",
            transition: { duration: 0.1 },
          });
        }

        // Back to normal
        await titleControls.start({
          x: 0,
          opacity: 1,
          skew: 0,
          textShadow: "none",
          transition: { duration: 0.1 },
        });
      }
    };

    glitchSequence();
  }, [titleControls]);

  // Subtitle subtle flicker effect
  useEffect(() => {
    const flickerSequence = async () => {
      while (true) {
        await subtitleControls.start({ opacity: 1 });
        await new Promise((resolve) =>
          setTimeout(resolve, Math.random() * 4000 + 1000)
        );
        await subtitleControls.start({
          opacity: 0.7,
          transition: { duration: 0.1 },
        });
        await subtitleControls.start({
          opacity: 1,
          transition: { duration: 0.1 },
        });
        await new Promise((resolve) => setTimeout(resolve, 100));
        await subtitleControls.start({
          opacity: 0.8,
          transition: { duration: 0.1 },
        });
        await subtitleControls.start({
          opacity: 1,
          transition: { duration: 0.1 },
        });
      }
    };

    flickerSequence();
  }, [subtitleControls]);

  return (
    <HelmetProvider>
      <div className="relative w-full h-screen bg-cover bg-center bg-no-repeat overflow-hidden bg-black">
        {/* Pixelated noise background */}
        <div className="absolute inset-0 opacity-20 z-10">
          <motion.div
            className="w-full h-full bg-black"
            animate={{
              backgroundImage: [
                'url("data:image/svg+xml,%3Csvg viewBox=%220 0 2000 2000%22 xmlns=%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cfilter id=%22a%22%3E%3CfeTurbulence type=%22fractalNoise%22 baseFrequency=%221%22 numOctaves=%223%22 stitchTiles=%22stitch%22%2F%3E%3C%2Ffilter%3E%3Crect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23a)%22%2F%3E%3C%2Fsvg%3E")',
              ],
            }}
          />
        </div>

        {/* Glitchy scan lines */}
        <div className="absolute inset-0 z-10 pointer-events-none">
          {Array.from({ length: 50 }).map((_, i) => (
            <motion.div
              key={i}
              className="w-full h-full bg-emerald-600 opacity-10"
              style={{ top: `${(i / 50) * 100}%` }}
              animate={{
                opacity: [0.05, 0.1, 0.05],
                scaleY: [1, 1.5, 1],
              }}
              transition={{
                repeat: Infinity,
                duration: 1,
                delay: i * 0.05,
              }}
            />
          ))}
        </div>

        <div className="absolute inset-0 z-20 flex flex-col items-center justify-center px-4 h-screen">
          <motion.h1
            animate={titleControls}
            className="text-display text-gradient text-center"
          >
            Game Over
          </motion.h1>

          <motion.p
            animate={subtitleControls}
            className="text-title text-text-secondary mt-4 text-center"
          >
            We're sorry, but the page you are looking for does not exist.
          </motion.p>

          <motion.button
            className="btn-secondary mt-8"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => (window.location.href = "/")}
          >
            Return Home
          </motion.button>
        </div>
      </div>
    </HelmetProvider>
  );
};

export default NotFound;
